/** Human-readable event names sent to Mixpanel. */
export const Events = {
  TASK: {
    CREATED_NEW_MOVE: 'Created: New Move Task',
    OPENED: 'Opened: Yard Task',
    STARTED: 'Started: Yard Task',
    UPDATED_ORIGIN_SPOT: 'Updated: Origin Spot',
    UPDATED_DESTINATION_SPOT: 'Updated: Destination Spot',
    CANCELED: 'Canceled: Yard Task',
    COMPLETED: 'Completed: Yard Task',
    FLAGGED_ISSUE: 'Flagged: Yard Task Issue',
    UNFLAGGED_ISSUE: 'Unflagged: Yard Task Issue',
    ADDED_COMMENT: 'Added: Yard Task Comment',
    ASSET_CHECK_MISSING: 'Asset Check: Missing from Yard',
    REPORTED_MISSING_ASSET: 'Reported: Missing Asset'
  },
  WAREHOUSE: {
    CHANGED: 'Changed: Warehouse'
  },
  TASK_BOARD: {
    FILTERED: 'Filtered: Yard Task Board',
    SWITCHED_LANE: 'Switched: Task Board Lane'
  }
} as const;

export interface YardTaskCreatedNewMovePayload {
  yard_task_id: string;
  yard_task_assignee: string;
}

/** Payload type for "Opened: Yard Task". */
export interface YardTaskOpenedPayload {
  yard_task_id: string;
  yard_task_type: string;
  yard_task_status: string;
  yard_task_assignee: string;
  yard_task_duedate: string;
  yard_task_description: string;
  yard_task_tags: unknown[];

  // Move/check-specific (optional in type; builder includes when present)
  asset_container_id?: string;
  origin_spot_id?: string;
  destination_spot_id?: string;
  check_entity_type?: string;
  check_entity_id?: string;
  yard_task_category?: string;
  yard_task_checklist?: unknown[];
}

/** Payload type for "Started: Yard Task". */
export interface YardTaskStartedPayload {
  yard_task_id: string;
  yard_task_type: string;
  yard_task_assignee?: string;
  previous_yard_task_assignee: string;
  yard_task_duedate: string;
  yard_task_description: string;
  yard_task_tags: unknown[];

  // Move/check-specific (optional in type; builder includes when present)
  asset_container_id?: string;
  origin_spot_id?: string;
  destination_spot_id?: string;
  check_entity_type?: string;
  check_entity_id?: string;
  yard_task_category?: string;
  yard_task_checklist?: unknown[];
}

/** Payload type for "Updated: Origin Spot". */
export interface YardTaskUpdatedOriginSpotPayload {
  yard_task_id: string;
  yard_task_status: string;
  yard_task_assignee: string;
  yard_task_duedate: string;
  yard_task_description: string;
  yard_task_tags: unknown[];
  asset_container_id?: string;
  origin_spot_id?: string;
  previous_asset_container_id: string;
  previous_origin_spot_id: string;
}

/** Payload type for "Updated: Destination Spot". */
export interface YardTaskUpdatedDestinationSpotPayload {
  yard_task_id: string;
  yard_task_status: string;
  yard_task_assignee: string;
  yard_task_duedate: string;
  yard_task_description: string;
  yard_task_tags: unknown[];
  destination_spot_id?: string;
  previous_destination_spot_id: string;
}

/** Payload type for "Canceled: Yard Task". */
export interface YardTaskCanceledPayload {
  yard_task_id: string;
  yard_task_type: string;
  yard_task_assignee: string;
  yard_task_duedate: string;
  yard_task_description: string;
  yard_task_tags: unknown[];
  /** Reason supplied in the cancel drawer; empty when cancelled without one. */
  cancellation_reason?: string;
}

/** Payload type for "Completed: Yard Task". */
export interface YardTaskCompletedPayload {
  yard_task_id: string;
  yard_task_type: string;
  yard_task_assignee: string;
  yard_task_duedate: string;
  yard_task_description: string;
  yard_task_tags: unknown[];
}

/** Payload type for "Flagged: Yard Task Issue". */
export interface YardTaskFlaggedIssuePayload {
  yard_task_id: string;
  yard_task_type: string;
  yard_task_tags: unknown[];
}

/** Payload type for "Unflagged: Yard Task Issue". */
export interface YardTaskUnflaggedIssuePayload {
  yard_task_id: string;
  yard_task_type: string;
  yard_task_tags: unknown[];
}

/** Payload type for "Added: Yard Task Comment". */
export interface YardTaskAddedCommentPayload {
  yard_task_id: string;
  yard_task_type: string;
  yard_task_assignee: string;
  yard_task_duedate: string;
  yard_task_description: string;
  yard_task_tags: unknown[];
}

/** Payload type for "Filtered: Task Board". */
export interface TaskBoardFilteredPayload {
  filter_type: string;
  is_filter_active: boolean;
}

/** Payload type for "Switched: Task Board Lane". */
export interface TaskBoardSwitchedLanePayload {
  switched_to_lane: string;
}

/** Payload type for "Asset Check: Missing from Yard". */
export interface AssetCheckMissingPayload {
  yard_task_id: string;
  asset_container_id: string;
  warehouse_id: string;
}

export type ReportedMissingAssetEntryPoint = 'task_detail' | 'inventory';

/** Payload type for "Reported: Missing Asset". */
export interface ReportedMissingAssetPayload {
  yard_task_id: string;
  asset_container_id: string;
  warehouse_id: string;
  entry_point: ReportedMissingAssetEntryPoint;
}

/** Map event keys to payload types (index signature for Satellite AnalyticsEventsMap contract). */
export type CirceEventsMap = {
  [Events.TASK.CREATED_NEW_MOVE]: YardTaskCreatedNewMovePayload;
  [Events.TASK.OPENED]: YardTaskOpenedPayload;
  [Events.TASK.STARTED]: YardTaskStartedPayload;
  [Events.TASK.UPDATED_ORIGIN_SPOT]: YardTaskUpdatedOriginSpotPayload;
  [Events.TASK.UPDATED_DESTINATION_SPOT]: YardTaskUpdatedDestinationSpotPayload;
  [Events.TASK.CANCELED]: YardTaskCanceledPayload;
  [Events.TASK.COMPLETED]: YardTaskCompletedPayload;
  [Events.TASK.FLAGGED_ISSUE]: YardTaskFlaggedIssuePayload;
  [Events.TASK.UNFLAGGED_ISSUE]: YardTaskUnflaggedIssuePayload;
  [Events.TASK.ADDED_COMMENT]: YardTaskAddedCommentPayload;
  [Events.TASK.ASSET_CHECK_MISSING]: AssetCheckMissingPayload;
  [Events.TASK.REPORTED_MISSING_ASSET]: ReportedMissingAssetPayload;
  [Events.WAREHOUSE.CHANGED]: void;
  [Events.TASK_BOARD.FILTERED]: TaskBoardFilteredPayload;
  [Events.TASK_BOARD.SWITCHED_LANE]: TaskBoardSwitchedLanePayload;
};
